import { useEffect, useMemo, useState } from 'react';

import { ErrorMessage } from '../../components/ErrorMessage';
import PagesFooter from '../../components/PagesFooter/PagesFooter';
import PagesHeader from '../../components/PagesHeader/PagesHeader';
import Spinner from '../../components/Spinner/Spinner';
import { usePagesContext } from '../../context/PagesContext';
import { scrollToFieldWithError } from '../../helpers/scrollToFieldWithError';
import { shouldRenderQuestion } from '../../helpers/shouldRenderKycQuestion';
import { useAppDispatch, useAppSelector } from '../../hooks';
import useComponentTracker from '../../hooks/useComponentTracker';
import useDefaultPagesAction from '../../hooks/useDefaultPagesAction';
import { useFieldValueClearer } from '../../hooks/useFieldValueClearer';
import useGetKycService from '../../hooks/useGetKycService';
import { usePageFocus } from '../../hooks/usePageFocusHook';
import usePagesConfig from '../../hooks/usePagesConfig';
import useSetBannerType from '../../hooks/useSetBannerType';
import useTranslation from '../../hooks/useTranslation';
import useValidation from '../../hooks/useValidation';
import { addOrUpdateFormField } from '../../store/applicationform';
import { selectAllKycFieldStates } from '../../store/applicationform/applicationFormSelectors';
import { WizardPageAction } from '../../store/wizard/wizard';
import { CITIZENSHIP_GROUP_ID, isSupportedQuestionType, Question } from '../../types/KycTypes';
import { convertToAnswers } from './convertToAnswers';
import KycGroup from './KycGroup';

const CoApplicantKycPage = () => {
    const translate = useTranslation();
    const dispatch = useAppDispatch();
    const defaultPagesAction = useDefaultPagesAction();
    const { coApplicantTotalSections } = usePagesConfig();
    const { currentPage, pageAction, setPageAction } = usePagesContext();
    const { kycData, allQuestions, isLoading } = useGetKycService(false);
    const [pageIndex, setPageIndex] = useState<number>(0);
    const [isPatchError, setIsPatchError] = useState(false);
    const kycAnswerFieldStates = useAppSelector(selectAllKycFieldStates)();
    usePageFocus(pageIndex);
    useComponentTracker('CoApplicantKycPage');
    useFieldValueClearer(kycData, kycAnswerFieldStates);

    useSetBannerType('bannerFour');

    const visibleQuestions = allQuestions
        .filter((question) => shouldRenderQuestion(question, allQuestions, kycAnswerFieldStates))
        .reduce((pages: Question[][], question) => {
            const lastPage = pages[pages.length - 1];

            if (
                lastPage &&
                ((question.groupId === CITIZENSHIP_GROUP_ID && lastPage[0].groupId === CITIZENSHIP_GROUP_ID) ||
                    (question.displayCondition?.questionId === lastPage[lastPage.length - 1].name &&
                        question.groupId === lastPage[lastPage.length - 1].groupId))
            ) {
                lastPage.push(question);
            } else {
                pages.push([question]);
            }

            return pages;
        }, []);

    // Special handling for back action to this page
    useEffect(() => {
        if (pageAction === 'back' && visibleQuestions.length > 0) {
            setPageAction('next');
            setPageIndex(visibleQuestions.length - 1);
        }
    }, [pageAction, visibleQuestions]);

    const currentGroup = useMemo(
        () => kycData?.groups.find((group) => group.id === visibleQuestions[pageIndex][0].groupId),
        [kycData, pageIndex, visibleQuestions],
    );

    const pageIndexQuestions = visibleQuestions[pageIndex ?? 0]?.map((question) => question);
    const requiredFields = pageIndexQuestions?.map((field) => 'KYC_' + field.groupId + '_' + field.name) ?? [];
    const validationErrors = useValidation(requiredFields, pageIndexQuestions);
    const hasUnsupportedQuestion = !!pageIndexQuestions?.some((question) => !isSupportedQuestionType(question.type));

    const onAction = async (action: WizardPageAction) => {
        let newPageIndex = pageIndex;

        if (action === 'next') {
            if (validationErrors.length) {
                // A question whose type we have no field for is required and unanswerable, so validation would block
                // here with nothing on screen the customer can fix. Say so instead of leaving "next" dead.
                if (hasUnsupportedQuestion) {
                    setIsPatchError(true);
                }
                scrollToFieldWithError(validationErrors);
                validationErrors.forEach((validationError) => {
                    dispatch(addOrUpdateFormField(validationError));
                });
                return false;
            }

            newPageIndex++;
        } else if (action === 'back' && newPageIndex > 0) {
            newPageIndex--;
        } else if (action === 'back' && newPageIndex === 0) {
            return defaultPagesAction('back');
        }

        if (kycData && visibleQuestions && newPageIndex >= visibleQuestions.length) {
            const answers = convertToAnswers(kycAnswerFieldStates);

            if (!answers.length && kycData?.groups.length) {
                // The answers themselves go out with the co-applicant submit; this only guards against carrying on
                // with an empty set. Show the error rather than throwing silently.
                setIsPatchError(true);
                throw new Error('Validation errors');
            }

            try {
                setIsPatchError(false);
                // `return await`, not `return`: a returned promise settles after the try block, so the catch below
                // was unreachable.
                return await defaultPagesAction(action);
            } catch (error) {
                setIsPatchError(true);
                throw error;
            }
        } else {
            setPageIndex(newPageIndex);
        }
        return true;
    };

    return (
        <>
            {isLoading || !kycData || !currentGroup ? (
                <Spinner size="xl" />
            ) : (
                <>
                    <PagesHeader
                        actions={['back']}
                        onAction={onAction}
                        currentPage={pageIndex === 0 ? currentPage : pageIndex}
                        currentSection={1}
                        numberOfSections={coApplicantTotalSections}
                        numberOfPagesInSection={visibleQuestions?.length !== 0 ? visibleQuestions.length : 7}
                        currentPageInSection={pageIndex}
                        sectionTitle={translate('field.section.kyc')}
                    />
                    <KycGroup kycData={kycData} group={currentGroup} questions={visibleQuestions[pageIndex]} />
                </>
            )}
            {isPatchError && (
                <ErrorMessage title={translate('page.error.title')} description={translate('page.error.description')} />
            )}
            <PagesFooter
                actions={['next']}
                onAction={onAction}
                currentSection={1}
                numberOfSections={coApplicantTotalSections}
                numberOfPagesInSection={visibleQuestions?.length !== 0 ? visibleQuestions.length : 7}
                currentPageInSection={pageIndex}
                sectionTitle={translate('field.section.kyc')}
            />
        </>
    );
};

export default CoApplicantKycPage;
